import React, { ReactNode, useRef } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Input, cn } from '@column/ui';

import { TRANSITION_CLOSE, TRANSITION_OPEN } from './constants';
import { inertWhenHidden } from './inert';

const CardCopyRow: React.FC<{ row: SelectedCardRow }> = ({ row }) => (
  <div className="@container/copy-row border-t border-gray-150 dark:border-white/[0.08]">
    <div className="grid items-start leading-4 @tablet/copy-row:grid-cols-[minmax(auto,220px)_auto] @tablet/copy-row:gap-4">
      <div className="px-4 pt-4 text-sm leading-4 font-medium whitespace-nowrap text-gray-600 @tablet/copy-row:p-4 dark:text-gray-300">
        {row.label}
      </div>
      <div className="px-1 pt-1 pb-2 @tablet/copy-row:py-2 @tablet/copy-row:pr-2 @tablet/copy-row:pl-6">
        <Input
          variant="copy"
          inputSize="sm"
          readOnly
          value={row.value}
          showCopyButton
          className={cn(
            '[&_input]:bg-transparent [&_input]:text-muted-foreground [&_input]:shadow-none [&_input]:[--input-border-color:transparent]',
            'hover:[&_input]:bg-white hover:[&_input]:shadow-[inset_0_1px_1px_rgb(0_0_0/0.075),inset_0_2px_4px_rgb(0_0_0/0.025)] hover:[&_input]:[--input-border-color:rgb(43_49_67/0.125)] dark:hover:[&_input]:bg-input/30',
            '[&_[aria-label=Copy]]:opacity-0 [&_[aria-label=Copy]]:transition-opacity focus-within:[&_[aria-label=Copy]]:opacity-100 hover:[&_[aria-label=Copy]]:opacity-100',
            row.monospaced && '[&_input]:font-mono',
            row.emphasized && '[&_input]:font-medium [&_input]:text-foreground'
          )}
        />
      </div>
    </div>
  </div>
);

export interface SelectedCardRow {
  label: string;
  value: string;
  monospaced?: boolean;
  emphasized?: boolean;
}

export interface SelectedCardProps {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  rows: SelectedCardRow[];
  onClear?: () => void;
  hasError?: boolean;
  isLoading?: boolean;
  alert?: string;
  alertTone?: 'error' | 'warning';
  className?: string;
}

// While collapsing, keeps rendering the last open content so it fades out with the card instead of emptying.
export const SelectedCard: React.FC<SelectedCardProps> = ({
  open,
  title,
  subtitle,
  icon,
  rows,
  onClear,
  hasError,
  isLoading,
  alert,
  alertTone = 'error',
  className,
}) => {
  const snapshot = useRef({ title, subtitle, icon, rows, hasError, isLoading, alert, alertTone });
  if (open) {
    snapshot.current = { title, subtitle, icon, rows, hasError, isLoading, alert, alertTone };
  }
  const view = open ? { title, subtitle, icon, rows, hasError, isLoading, alert, alertTone } : snapshot.current;
  const isWarning = !view.hasError && Boolean(view.alert) && view.alertTone === 'warning';

  return (
    <div
      aria-hidden={!open}
      {...inertWhenHidden(!open)}
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-lg bg-card text-card-foreground shadow-surface-raised',
        'dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]',
        'transition-all motion-reduce:transition-none',
        open ? TRANSITION_OPEN : TRANSITION_CLOSE,
        open ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
        (view.hasError || view.alert) &&
          !isWarning &&
          'shadow-[0_0_0_1px_var(--color-destructive)] ring-[3px] ring-destructive/20 dark:shadow-[0_0_0_1px_var(--color-destructive)] dark:ring-destructive/40',
        isWarning &&
          'shadow-[0_0_0_1px_var(--color-orange-400)] ring-[3px] ring-orange-400/20 dark:shadow-[0_0_0_1px_var(--color-orange-500)] dark:ring-orange-500/30',
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {!view.isLoading && view.icon && (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-400 shadow-[0_0_0_1px_var(--color-gray-150)] dark:bg-white/5 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
                {view.icon}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-foreground">
                {view.isLoading ? 'Loading…' : view.title}
              </span>
              {!view.isLoading && view.subtitle && (
                <span className="truncate text-xs text-muted-foreground">{view.subtitle}</span>
              )}
            </div>
          </div>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Clear selection"
              className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.Cross className="[--icon-size:16px]" />
            </button>
          )}
        </div>

        {!view.isLoading && view.rows.map((row) => <CardCopyRow key={row.label} row={row} />)}

        {view.alert && (
          <div
            className={cn(
              'flex items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-xs font-medium dark:border-white/[0.08]',
              view.alertTone === 'warning'
                ? 'bg-orange-500/5 text-orange-600 dark:text-orange-300'
                : 'bg-destructive/5 text-destructive'
            )}
          >
            <Icon.Warning className="shrink-0 [--icon-size:14px]" />
            <span className="text-pretty">{view.alert}</span>
          </div>
        )}
      </div>
    </div>
  );
};
